import React, { useCallback, useEffect, useState } from 'react';
import { Play, Image as ImageIcon, Video, Download, X, Camera } from 'lucide-react';
import './Gallery.css';
import usePageTitle from '../hooks/usePageTitle';
import galleryVideo from '../assets/images/gallery-video.svg';

type MediaType = 'image' | 'video';

interface Category {
  id: string;
  name: string;
}

interface GalleryItem {
  id: number;
  title: string;
  category: string;
  type: MediaType;
  image: string;
  description: string;
}

interface ModalState {
  type: MediaType;
  src?: string;
  title: string;
  description: string;
}

const categories: Category[] = [
  { id: 'all', name: 'All' },
  { id: 'live', name: 'Live' },
  { id: 'studio', name: 'Studio' },
  { id: 'portraits', name: 'Portraits' },
  { id: 'videos', name: 'Videos' },
];

const galleryItems: GalleryItem[] = [
  {
    id: 5,
    title: 'Recording Studio',
    category: 'studio',
    type: 'video',
    image: '/assets/gallery-thumbnail-5.svg',
    description: 'Inside the room where the debut took shape.',
  },
  {
    id: 1,
    title: 'Live Performance',
    category: 'live',
    type: 'image',
    image: '/assets/gallery-thumbnail-1.svg',
    description: 'Raw energy from the stage.',
  },
  {
    id: 6,
    title: 'Music Video',
    category: 'videos',
    type: 'video',
    image: galleryVideo,
    description: 'A first look at the visual world of The Kults.',
  },
  {
    id: 2,
    title: 'Stage Setup',
    category: 'live',
    type: 'image',
    image: '/assets/gallery-thumbnail-2.svg',
    description: 'Equipment and stage preparation.',
  },
  {
    id: 3,
    title: 'Crowd Energy',
    category: 'live',
    type: 'image',
    image: '/assets/gallery-thumbnail-3.svg',
    description: 'The faces that make the noise.',
  },
  {
    id: 4,
    title: 'Backstage',
    category: 'studio',
    type: 'image',
    image: '/assets/gallery-thumbnail-4.svg',
    description: 'Behind the scenes, before the lights.',
  },
];

const Gallery: React.FC = () => {
  usePageTitle('Gallery - The Kults');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modal, setModal] = useState<ModalState | null>(null);

  const filteredItems =
    selectedCategory === 'all'
      ? galleryItems
      : galleryItems.filter((item) => item.category === selectedCategory);

  const getCategoryCount = (categoryId: string): number =>
    categoryId === 'all'
      ? galleryItems.length
      : galleryItems.filter((item) => item.category === categoryId).length;

  const openItem = useCallback((item: GalleryItem): void => {
    setModal({
      type: item.type,
      src: item.type === 'image' ? item.image : undefined,
      title: item.title,
      description: item.description,
    });
  }, []);

  const closeModal = useCallback((): void => {
    setModal(null);
  }, []);

  // Accessibility: close on Escape and lock background scroll while open.
  useEffect(() => {
    if (!modal) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [modal, closeModal]);

  return (
    <div className="gallery-page">
      <section className="gallery-hero">
        <div className="container">
          <span className="gallery-hero-eyebrow">
            <Camera size={14} />
            The Archive
          </span>
          <h1>GALLERY</h1>
          <p>Inside the world of The Kults — stage, studio, and everything between.</p>
        </div>
      </section>

      <section className="gallery-content">
        <div className="container">
          <div className="gallery-filters" role="tablist" aria-label="Gallery categories">
            {categories.map((category) => {
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  className={`filter-btn ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                  role="tab"
                  aria-selected={isActive}
                >
                  {category.id === 'videos' ? (
                    <Video size={16} />
                  ) : (
                    <ImageIcon size={16} />
                  )}
                  {category.name}
                  <span className="filter-count">{getCategoryCount(category.id)}</span>
                </button>
              );
            })}
          </div>

          {filteredItems.length === 0 ? (
            <div className="gallery-empty">
              <Camera size={48} />
              <h3>Nothing Here Yet</h3>
              <p>
                This collection is still being shot. Check back soon — the
                archive grows with every show.
              </p>
            </div>
          ) : (
            <div className="gallery-grid">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="gallery-item"
                  data-category={item.category}
                  data-type={item.type}
                  onClick={() => openItem(item)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${item.title}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openItem(item);
                    }
                  }}
                >
                  <div className="gallery-item-media">
                    <img src={item.image} alt={item.title} loading="lazy" />
                    {item.type === 'video' && (
                      <div className="video-overlay">
                        <Play size={40} />
                      </div>
                    )}
                  </div>
                  <div className="item-overlay">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    {item.type === 'image' && (
                      <a
                        className="download-btn"
                        href={item.image}
                        download
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download size={16} />
                        Download
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {modal && (
        <div
          className="gallery-modal"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label={modal.title}
        >
          <button
            className="close-btn"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            aria-label="Close modal"
          >
            <X />
          </button>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            {modal.type === 'image' ? (
              <img src={modal.src} alt={modal.title} />
            ) : (
              <div className="video-placeholder">
                <Play size={64} style={{ marginRight: 16 }} />
                Video player coming soon
              </div>
            )}
            <div className="modal-title">{modal.title}</div>
            <div className="modal-desc">{modal.description}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
